import React, { useEffect, useRef, useState } from 'react';
import { ConfigProvider, Table } from 'tea-component';
import { SortBy } from 'tea-component/lib/table/addons/sortable';
import { getChartsShowCount, getShowCount } from '@op/utils';
import { getHashtagTableDate } from '@op/services/hashtag';
import { dealwithTableData } from '../../utils';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import ChannelLabel from '@op/components/ChannelLabel';
import Sentiment from '@/pages/opinion/components/Snippets/Sentiment';
import TableExpandableDetail from '../../../../../components/TableExpandableDetail';
import FilterBoxCommon from '@/pages/opinion/components/FilterBoxCommon';
import { getLocale, useIntl } from 'umi';
import CardSlot from '@/pages/opinion/components/CardSlot';
import { getTeaLocale } from '@/pages/home/utils/lang';
import './index.scss';
import { LanguageType } from '@/pages/opinion/types/common';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { getNameText } from '@/pages/opinion/utils/languageValue';
import BubbleCommon, { hashtagTotalPotentialImpressionTips } from '@op/components/BubbleCommon';
import styles from '../../styles/tableCommon.module.scss';
import { NO_DATE } from '@op/constants';

const { expandable, pageable, sortable, autotip } = Table.addons;

const columnsWidth = 190;
export default function HashtagTable({
  filter,
  languageType,
  setLanguageType,
  byChannel,
  Sentiment_Rating,
}: {
  filter: any;
  languageType: LanguageType;
  setLanguageType: (value: LanguageType) => void;
  byChannel: boolean;
  Sentiment_Rating?: boolean;
}) {
  const { formatMessage } = useIntl();
  const { downloadLegacy, downloading } = useDownloadParams();
  const [expandedKeys, setExpandedKeys] = useState<Array<string>>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(false);
  const [sort, setSort] = useState<SortBy<any>>({
    by: 'mentions',
    order: 'desc',
  });
  const [tableRecords, setTableRecord] = useState<Array<any>>([]);
  const [pageInfo, setPageInfo] = useState({ pageSize: 10, pageIndex: 1 });
  const splitText = useRef('#_to_#');

  const getTableData = (newPageInfo?: {
    pageIndex: number;
    pageSize: number;
  }) => {
    if (Object.keys(filter).length === 0 || loading) return;
    setExpandedKeys([]);
    const { pageSize, pageIndex } = newPageInfo || pageInfo;
    setLoading(true);
    setApiError(false);
    const { by, order } = sort;
    setTableRecord([]);
    getHashtagTableDate({
      ...filter,
      sort_model: order,
      sort_item: by,
      page: pageIndex,
      page_size: pageSize,
      by_channel: byChannel,
    })
      .then((resultData: any) => {
        const { new_list, total, old_list, max, hashtag_channel_split } =
          resultData || {};
        splitText.current = hashtag_channel_split;
        // 固定sentiment跟non_negative_ratio 最大值写死
        const newMax = {
          ...max,
          sentiment: 5,
          non_negative_ratio: 1,
        };
        setTableRecord(dealwithTableData(new_list, old_list, newMax));
        setTotal(total || 0);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getTableData();
  }, [pageInfo, sort]);

  useEffect(() => {
    // 这里用浅拷贝，因为我不想因为我设置了pageInfo导致触发上面那个useEffect
    const newPageInfo = Object.assign(pageInfo, { pageIndex: 1 });
    setPageInfo(newPageInfo);
    getTableData(newPageInfo);
  }, [filter, byChannel]);

  const tableColumn: any = () => {
    const columns = [
      {
        key: 'name',
        header: formatMessage({ id: 'Hashtag' }),
        width: 200,
        fixed: 'left',
        render: (item: any) => {
          const name = getNameText(item, languageType) || item.name;
          if (byChannel) {
            const nameInfo = name?.includes(splitText.current)
              ? name
              : item.name;
            const [channel, newName] = nameInfo?.split(splitText.current) || [];

            return (
              <>
                <ChannelLabel
                  showText={false}
                  channel_name={channel}
                ></ChannelLabel>
                <span className={styles['name-text']}>#{newName}</span>
              </>
            );
          }
          return `#${name}`;
        },
      },
      {
        key: 'mentions',
        header: formatMessage({ id: 'Mentions' }),
        align: 'right',
        width: columnsWidth,
        render: ({ mentions, mentions_than, mentions_percentage }: any) => {
          return tableColumnRender(
            getShowCount(mentions) as any,
            mentions_than,
            mentions_percentage,
          );
        },
      },
    ];
    if (Sentiment_Rating) {
      return columns;
    }

    return [
      ...columns,
      {
        key: 'followers',
        header: (
          <BubbleCommon tipsMap={hashtagTotalPotentialImpressionTips} showIcon={false}>
            {formatMessage({ id: 'Total Potential Impressions' })}
          </BubbleCommon>
        ),
        align: 'right',
        width: 270,
        render: ({
          followers,
          followers_than,
          followers_percentage,
        }: any) =>
          tableColumnRender(
            getShowCount(followers) as any,
            followers_than,
            followers_percentage,
          ),
      },
      {
        key: 'views',
        header: formatMessage({ id: 'Views-2' }),
        align: 'right',
        width: columnsWidth,
        render: ({
          views,
          views_than,
          views_percentage,
        }: any) =>
          tableColumnRender(
            getShowCount(views) as any,
            views_than,
            views_percentage,
          ),
      },
      {
        key: 'non_negative_ratio',
        header: formatMessage({ id: 'Non Negative%' }),
        align: 'right',
        width: columnsWidth,
        render: ({
          non_negative_ratio,
          non_negative_ratio_than,
          non_negative_ratio_percentage,
        }: any) =>
          tableColumnRender(
            `${(non_negative_ratio * 100).toFixed(2)}%`,
            non_negative_ratio_than,
            non_negative_ratio_percentage,
          ),
      },
      {
        key: 'sentiment',
        header: formatMessage({ id: 'Sentiment' }),
        align: 'right',
        width: columnsWidth,
        render: ({ sentiment, sentiment_than, sentiment_percentage }: any) => {
          const element = (
            <Sentiment
              iconClassName={styles['icon-info']}
              sentiment={sentiment}
            ></Sentiment>
          );
          return tableColumnRender(
            element,
            sentiment_than,
            sentiment_percentage,
          );
        },
      },
      {
        key: 'negative_mentions',
        header: formatMessage({ id: 'Negative' }),
        align: 'right',
        width: columnsWidth,
        render: ({
          negative_mentions,
          negative_mentions_than,
          negative_mentions_percentage,
        }: any) =>
          tableColumnRender(
            getChartsShowCount(negative_mentions),
            negative_mentions_than,
            negative_mentions_percentage,
          ),
      },
    ];
  };

  const tableColumnRender = (
    value: JSX.Element | string = '100',
    percentage = 50,
    percentageBar: any,
  ) => {
    return (
      <div className={styles['table-cell-info']}>
        {/* 进度条，使用定位处理该内容 */}
        <div
          className={styles['percentage-info']}
          style={{ width: `${percentageBar}%` || '0%' }}
        ></div>
        <div className={styles['value-box']}>
          <PercentageItem percentage={percentage}>
            {(element: any) => (
              <>
                {value} {element}
              </>
            )}
          </PercentageItem>
        </div>
      </div>
    );
  };
  const locale = getTeaLocale(getLocale());
  return (
    <div className={styles['hashtag-table-wrapper']}>
      <FilterBoxCommon
        left={[{ key: 'language', defaultValue: 'origin' }]}
        downloadLoading={downloading}
        right={[
          {
            key: 'download',
            event: {
              onClick: () => {
                const downloadLimit = 150000; // 下载限制条数
                downloadLegacy({
                  limit: downloadLimit,
                  params: {
                    ...filter,
                    sort_model: sort.order,
                    sort_item: sort.by,
                    page: 1,
                    page_size: 1000,
                    by_channel: byChannel,
                    splitText: splitText.current,
                    hideSentimentRating: Sentiment_Rating,
                  },
                  downloadFunc: 'getHashtagTableDate',
                  sheetName: 'Hashtag',
                  total,
                  simpleName: 'Hashtag',
                });
              },
            },
          },
        ]}
        onChange={(value, key) => {
          setLanguageType(key ? value[key] : 'origin');
        }}
      ></FilterBoxCommon>
      <ConfigProvider locale={locale}>
        <CardSlot>
          {/* dbo-table-sticky表头吸顶，默认在顶部，如果需要在筛选器吸顶下方，需要加--withfilter */}
          <Table
            className="dbo-table-sticky dbo-table-sticky--withfilter dbo-hashtag-table"
            bordered="all"
            records={tableRecords}
            recordKey="name"
            columns={tableColumn()}
            addons={[
              expandable({
                expandedKeys,
                rowExpand: true,
                targetColumnKey: 'name',
                onExpandedKeysChange: (keys, { event }) => {
                  event.stopPropagation();
                  setExpandedKeys(keys);
                },
                iconRender: (element, _context) => {
                  return <div className="dbo-hashtag-table-tag">{element}</div>;
                },
                render({ name }) {
                  if (splitText.current && name.includes(splitText.current)) {
                    const [_channel, newName] =
                      name?.split(splitText.current) || [];
                    name = newName;
                  }
                  return (
                    <TableExpandableDetail
                      noShowChannel={byChannel}
                      language={languageType}
                      params={{
                        ...filter,
                        keyword: [name],
                      }}
                      sortItem='engagement'
                    ></TableExpandableDetail>
                  );
                },
              }),
              sortable({
                // 这两列支持排序，其中 age 列优先倒序，mail 采用自定义排序方法
                columns: [
                  'mentions',
                  'non_negative_ratio',
                  'negative_mentions',
                  'sentiment',
                  'followers',
                  'views'
                ].map((item) => ({
                  key: item,
                  prefer: 'desc',
                })),
                value: [sort],
                onChange: (_value, { sort }) => {
                  setSort(sort);
                },
              }),
              pageable({
                recordCount: total,
                pageIndex: pageInfo.pageIndex,
                pageSize: pageInfo.pageSize,
                onPagingChange: ({ pageSize, pageIndex }) => {
                  setPageInfo({
                    pageIndex: pageIndex || 1,
                    pageSize: pageSize || 10,
                  });
                },
              }),
              autotip({
                isLoading: loading,
                emptyText: formatMessage({ id: NO_DATE.SELECTION }),
                loadingText: '',
              }),
            ]}
          />
        </CardSlot>
      </ConfigProvider>
    </div>
  );
}
